import { useEffect, useState, useCallback } from "react";
import { createNewUser, setRolesOfUser } from "services/authentication";
import {
  getAuthUser,
  disableAuthUser,
  enableAuthUser,
} from "services/authentication";
import { useFormik } from "formik";
import * as Yup from "yup";

import {
  Grid,
  Row,
  Col,
  Form,
  Input,
  Card,
  Text,
  Notification,
  useToaster,
  ButtonToolbar,
  Table,
  Button,
  PasswordInput,
  CheckboxGroup,
  Checkbox,
} from "rsuite";
import "rsuite/dist/rsuite.min.css";

import MDBox from "components/atoms/MDBox";
import DashboardLayout from "components/templates/DashboardLayout";
import Topnav from "components/organisms/Topnav";
import DashboardFooter from "components/organisms/Footer/DashboardFooter";
import PermissionDenied from "components/organisms/PermissionDenied";
//import ErrorSnackbar from "components/molecules/Notifications/ErrorSnackbar";
//import SuccessSnackbar from "components/molecules/Notifications/SuccessSnackbar";
//import InvalidOperation from "components/organisms/InvalidOperation";
//import IncompleteOperation from "components/organisms/IncompleteOperation";
//import SubmissionProgress from "components/organisms/SubmissionProgress";

import { AllowedTo } from "react-abac";
import { useAuthContext } from "context/AuthContext";
import { useDocument } from "hooks/useDocument";
import { Permission } from "models/abac";
import tablestyles from "pages/settings/users2/tablestyles.module.css";
import EditIcon from "@rsuite/icons/Edit";
import CheckOutlineIcon from "@rsuite/icons/CheckOutline";
import CloseOutlineIcon from "@rsuite/icons/CloseOutline";

const { Column, HeaderCell, Cell } = Table;

/* [0.1] Validation schema and initial values for the formik form */
const validationSchema = Yup.object().shape({
  displayName: Yup.string()
    .required("Please provide a user name")
    .nullable(true),
  email: Yup.string().required("Please provide an email").nullable(true),
  organization: Yup.string()
    .required("Please provide the orgnization name")
    .nullable(true),
  password: Yup.string()
    .required("Password is required")
    .min(8, "Must be at least 8 characters")
    .matches(/[A-Z]/, "Must contain an uppercase letter")
    .matches(/[a-z]/, "Must contain a lowercase letter")
    .matches(/[0-9]/, "Must contain a number")
    .matches(/[@$!%*?&]/, "Must contain a special character"),
  //roles: Yup.array().min(1, "Please select at least one role"),
});

const localDefaults = {
  displayName: "",
  email: "",
  organization: "",
  password: "",
  roles: [],
  documentId: "",
};

const passwordInstructions = [
  { id: 1, text: "Must be at least 8 characters" },
  { id: 2, text: "Must contain an uppercase letter" },
  { id: 3, text: "Must contain a lowercase letter" },
  { id: 4, text: "Must contain a number" },
  { id: 5, text: "Must contain a special character" },
];

function Users() {
  const { user } = useAuthContext();

  const { createDoc, retrieveDocs, updateDoc, serverTimestamp } = useDocument();
  const modePermission = Permission.VIEW_ADMIN_DASHBOARD;
  const [mode, setMode] = useState("NEW");
  const [tableData, setTableData] = useState([]);
  const [buttonLoading, setButtonLoading] = useState(false);

  /* [1] Form */
  // 1.1 Formik initialization
  const [formikInitialValues, setFormikInitialValues] = useState(localDefaults);
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: formikInitialValues,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      handleSubmit(values);
    },
  });

  // 1.2 Form submission handler
  const handleSubmit = async (values) => {
    try {
      if (mode === "NEW") {
        setButtonLoading(true);

        const result = await createNewUser({
          email: values.email,
          organization: values.organization,
          password: values.password,
          emailVerified: true,
          displayName: values.displayName,
          //photoURL: "",
        });
        //console.log("createNewUser result:", result);

        const userRecord = result.data;
        const userId = userRecord.uid;

        await setRolesOfUser({
          roles: [...values.roles, "MEMBER"],
          userId: userId,
        });

        await createDoc(
          "users",
          {
            email: values.email,
            organization: values.organization,
            displayName: values.displayName,
            roles: [...values.roles, "MEMBER"],
            root: false,
            system: false,
          },
          user.uid,
          userId,
        );

        toaster.push(messageSubmitNew, { placement: "bottomEnd" });
        setButtonLoading(false);
      } else {
        setButtonLoading(true);

        const valueRoles = (values.roles = values.roles.filter(
          (role) => role.length > 0,
        ));

        await updateDoc("users", values.documentId, {
          displayName: values.displayName,
          roles: [...valueRoles, "MEMBER"],
          modifiedAt: serverTimestamp(),
          modifiedBy: user.uid,
        });
        toaster.push(messageSubmitEdit, { placement: "bottomEnd" });
        setButtonLoading(false);
      }

      formik.resetForm({
        values: {
          displayName: "",
          organization: "",
          email: "",
          password: "",
          roles: [],
          documentId: "",
        },
      });
      setMode("NEW");
      updateTable();
    } catch (err) {
      console.log(err);
      //dispatchError(err);
    }
  };

  // 1.3 Notification
  const toaster = useToaster();
  const messageSubmitNew = (
    <Notification
      type="success"
      header="CREATE MEMBER ACCOUNT"
      closable
      backgroundColor={"#CAEEFB"}
    >
      <Text size="md">Member account created successfully!</Text>
      <hr />
      <ButtonToolbar display="flex" justifyContent="flex-end">
        <Button appearance="primary" onClick={() => toaster.clear()}>
          Ok
        </Button>
      </ButtonToolbar>
    </Notification>
  );

  const messageSubmitEdit = (
    <Notification
      type="success"
      header="UPDATEMEMBER PROFILE "
      closable
      backgroundColor={"#D9F2D0"}
    >
      <Text size="md">Member profile updated successfully!</Text>
      <hr />
      <ButtonToolbar display="flex" justifyContent="flex-end">
        <Button appearance="primary" onClick={() => toaster.clear()}>
          Ok
        </Button>
      </ButtonToolbar>
    </Notification>
  );

  const messageAccountDeactivated = (
    <Notification
      type="success"
      header="DEACTIVATE MEMBER ACCOUNT"
      closable
      backgroundColor={"#FBE3D6"}
    >
      <Text size="md">Member account deactivated!</Text>
      <hr />
      <ButtonToolbar display="flex" justifyContent="flex-end">
        <Button appearance="primary" onClick={() => toaster.clear()}>
          Ok
        </Button>
      </ButtonToolbar>
    </Notification>
  );

  const messageAccountActivated = (
    <Notification
      type="success"
      header="ACTIVATE MEMBER ACCOUNT"
      closable
      backgroundColor={"#FBE3D6"}
    >
      <Text size="md">Member account activated successfully!</Text>
      <hr />
      <ButtonToolbar display="flex" justifyContent="flex-end">
        <Button appearance="primary" onClick={() => toaster.clear()}>
          Ok
        </Button>
      </ButtonToolbar>
    </Notification>
  );

  /* [2] Table */
  const [sortColumn, setSortColumn] = useState(null);
  const [sortType, setSortType] = useState(null);
  const [tableLoading, setTableLoading] = useState(false);

  // 2.1 Sample data for the table
  // 2.2 Function to sort the table data based on the current sort column and type
  const getData = () => {
    if (sortColumn && sortType) {
      return tableData.sort((a, b) => {
        let x = a[sortColumn];
        let y = b[sortColumn];
        if (typeof x === "string") {
          x = x.charCodeAt();
        }
        if (typeof y === "string") {
          y = y.charCodeAt();
        }
        if (sortType === "asc") {
          return x - y;
        } else {
          return y - x;
        }
      });
    }
    return tableData;
  };

  const handleSortColumn = (sortColumn, sortType) => {
    setTableLoading(true);
    setTimeout(() => {
      setTableLoading(false);
      setSortColumn(sortColumn);
      setSortType(sortType);
    }, 500);
  };

  const addUserStatus = useCallback((usersData) => {
    const userStatusPromises = usersData.map(async (doc, index) => {
      const result = await getAuthUser({ uid: doc.id });
      const userRecord = result.data;

      const filteredRoles = doc.data.roles.filter((item) => item !== "MEMBER");

      return {
        id: index + 1,
        documentId: doc.id,
        displayName: doc.data.displayName,
        email: doc.data.email,
        organization: doc.data.organization,
        roles: filteredRoles.join(", "),
        status: userRecord.disabled ? "Deactivated" : "Active",
      };
    });

    return Promise.all(userStatusPromises);
  }, []);

  const fetchUsers = useCallback(async () => {
    const usersQueries = {
      whereQueries: [
        {
          field: "deletedAt",
          condition: "==",
          value: null,
        },
      ],
      orderByQueries: [
        {
          field: "organization",
          direction: "asc",
        },
        {
          field: "displayName",
          direction: "asc",
        },
      ],
    };

    const usersData = await retrieveDocs("users", usersQueries);
    const tableData = await addUserStatus(usersData);

    setTableData(tableData);
  }, [retrieveDocs, addUserStatus]);

  const updateTable = useCallback(async () => {
    setTableLoading(true);
    setTimeout(() => {
      fetchUsers();

      setTableLoading(false);
    }, 2000);
  }, [fetchUsers]);

  useEffect(() => {
    updateTable();
  }, [updateTable]);

  return (
    <DashboardLayout>
      <Topnav title="SSTD Project Members" />
      <AllowedTo perform={modePermission} no={() => <PermissionDenied />}>
        <>
          <MDBox mb={4} />
          <Grid fluid>
            <Row gutter={[9, 18]}>
              <Col span={{ xs: 24 }}>
                <Card size="md" shaded>
                  <Card.Header
                    backgroundColor={"blue"}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      paddingBottom: "6px",
                    }}
                  >
                    {mode === "NEW" ? (
                      <Text
                        size="lg"
                        style={{
                          textAlign: "left",
                          width: "100%",
                          color: "white",
                        }}
                      >
                        Create New Member Record
                      </Text>
                    ) : (
                      <Text
                        size="lg"
                        style={{
                          textAlign: "left",
                          width: "100%",
                          color: "white",
                        }}
                      >
                        Edit Member Record
                      </Text>
                    )}
                  </Card.Header>
                  <Card.Body>
                    <Form onSubmit={formik.handleSubmit}>
                      <Form.Stack fluid>
                        <Grid fluid>
                          <Row gutter={[24, 24]}>
                            {/* User Name */}
                            <Col
                              span={{ xs: 6 }}
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Text size="md">Name</Text>
                            </Col>
                            <Col
                              span={{ xs: 18 }}
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Input
                                name="displayName"
                                label="displayName"
                                value={formik.values.displayName}
                                error={formik.errors.displayName}
                                onChange={(value) =>
                                  formik.setFieldValue("displayName", value)
                                }
                              />
                              <Form.ErrorMessage
                                show={!!formik.errors.displayName}
                                placement="bottomStart"
                              >
                                {formik.errors.displayName}
                              </Form.ErrorMessage>
                            </Col>

                            {/* Email */}
                            <Col
                              span={{ xs: 6 }}
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Text size="md">Email</Text>
                            </Col>
                            <Col
                              span={{ xs: 18 }}
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Input
                                name="email"
                                label="Email"
                                value={formik.values.email}
                                error={formik.errors.email}
                                onChange={(value) =>
                                  formik.setFieldValue("email", value)
                                }
                                disabled={mode === "NEW" ? false : true}
                              />
                              <Form.ErrorMessage
                                show={!!formik.errors.email}
                                placement="bottomStart"
                              >
                                {formik.errors.email}
                              </Form.ErrorMessage>
                            </Col>

                            {/* Organization */}
                            <Col
                              span={{ xs: 6 }}
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Text size="md">Organization</Text>
                            </Col>
                            <Col
                              span={{ xs: 18 }}
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Input
                                name="organization"
                                label="Organization"
                                value={formik.values.organization}
                                error={formik.errors.organization}
                                onChange={(value) =>
                                  formik.setFieldValue("organization", value)
                                }
                              />
                              <Form.ErrorMessage
                                show={!!formik.errors.organization}
                                placement="bottomStart"
                              >
                                {formik.errors.organization}
                              </Form.ErrorMessage>
                            </Col>

                            {/* Password */}
                            <Col
                              span={{ xs: 6 }}
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Text size="md">Password</Text>
                            </Col>
                            <Col
                              span={{ xs: 18 }}
                              display="flexColumn"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <PasswordInput
                                name="password"
                                label="Password"
                                type="password"
                                value={formik.values.password}
                                error={formik.errors.password}
                                onChange={(value) =>
                                  formik.setFieldValue("password", value)
                                }
                                disabled={mode === "NEW" ? false : true}
                              />
                              <Form.ErrorMessage
                                show={!!formik.errors.password}
                                placement="bottomStart"
                              >
                                {formik.errors.password}
                              </Form.ErrorMessage>
                              <Card
                                style={{
                                  paddingLeft: "12px",
                                  paddingTop: "6px",
                                }}
                                bordered={false}
                              >
                                {passwordInstructions.map((message) => (
                                  <Text
                                    key={message.id}
                                    size="xs"
                                    style={{ color: "gray" }}
                                  >
                                    {message.text}
                                  </Text>
                                ))}
                              </Card>
                            </Col>

                            {/* Roles */}
                            <Col
                              span={{ xs: 6 }}
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Text size="md">Role</Text>
                            </Col>
                            <Col
                              span={{ xs: 18 }}
                              display="flexRow"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <CheckboxGroup
                                name="roles"
                                value={formik.values.roles}
                                onChange={(values) => {
                                  formik.setFieldValue("roles", values);
                                }}
                                inline
                              >
                                <Checkbox value="ADMIN">
                                  <Text size="sm">ADMIN</Text>
                                </Checkbox>
                                <span style={{ width: "4px" }} />
                                {/*<Checkbox value="MEMBER">
                                  <Text size="sm">MEMBER</Text>
                                </Checkbox>*/}
                              </CheckboxGroup>
                              <Form.ErrorMessage
                                show={!!formik.errors.roles}
                                placement="bottomStart"
                              >
                                {formik.errors.roles}
                              </Form.ErrorMessage>
                            </Col>

                            {/* Submit and Cancel */}
                            <Col
                              span={{ xs: 24 }}
                              display="flex"
                              justifyContent="flex-end"
                              alignItems="center"
                            >
                              <Button
                                appearance="primary"
                                color="orange"
                                onClick={() => {
                                  formik.resetForm({
                                    values: {
                                      displayName: "",
                                      email: "",
                                      organization: "",
                                      password: "",
                                      roles: [],
                                      documentId: "",
                                    },
                                  });
                                  setMode("NEW");
                                }}
                              >
                                Cancel
                              </Button>
                              <span style={{ width: "4px" }} />
                              <Button
                                appearance="primary"
                                type="submit"
                                loading={buttonLoading}
                              >
                                Submit
                              </Button>
                            </Col>
                          </Row>
                        </Grid>
                      </Form.Stack>
                    </Form>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Grid>
          <MDBox mb={4} />
          <Grid fluid>
            <Row gutter={[9, 18]}>
              <Col span={{ xs: 24 }}>
                <Card size="md" shaded>
                  <Card.Header backgroundColor={"green"}>
                    <Text
                      size="lg"
                      style={{
                        textAlign: "left",
                        width: "100%",
                        color: "white",
                        paddingBottom: "6px",
                      }}
                    >
                      Enrolled Members
                    </Text>
                  </Card.Header>
                  <Card.Body>
                    <Table
                      height={400}
                      data={getData()}
                      sortColumn={sortColumn}
                      sortType={sortType}
                      onSortColumn={handleSortColumn}
                      loading={tableLoading}
                      bordered={true}
                      locale={{
                        emptyMessage: "",
                      }}
                    >
                      {/* Id */}
                      <Column width={60} align="center" sortable>
                        <HeaderCell className={tablestyles.customHeader}>
                          Id
                        </HeaderCell>
                        <Cell dataKey="id" className={tablestyles.customCell} />
                      </Column>

                      {/* Name */}
                      <Column flexGrow={1} align="center" sortable>
                        <HeaderCell className={tablestyles.customHeader}>
                          Name
                        </HeaderCell>
                        <Cell
                          dataKey="displayName"
                          className={tablestyles.customCell}
                        />
                      </Column>

                      {/* Email */}
                      <Column flexGrow={2} align="center" sortable>
                        <HeaderCell className={tablestyles.customHeader}>
                          Email
                        </HeaderCell>
                        <Cell
                          dataKey="email"
                          className={tablestyles.customCell}
                          style={{
                            whiteSpace: "normal",
                            wordWrap: "break-word",
                          }}
                        />
                      </Column>

                      {/* Organization */}
                      <Column flexGrow={1} align="center" sortable>
                        <HeaderCell className={tablestyles.customHeader}>
                          Org.
                        </HeaderCell>
                        <Cell
                          dataKey="organization"
                          className={tablestyles.customCell}
                          style={{
                            whiteSpace: "normal",
                            wordWrap: "break-word",
                          }}
                        />
                      </Column>

                      {/* Roles */}
                      <Column flexGrow={1} align="center" sortable>
                        <HeaderCell className={tablestyles.customHeader}>
                          Roles
                        </HeaderCell>
                        <Cell
                          dataKey="roles"
                          className={tablestyles.customCell}
                          style={{
                            whiteSpace: "normal",
                            wordWrap: "break-word",
                          }}
                        />
                      </Column>

                      {/* View */}
                      <Column flexGrow={1} align="center" sortable>
                        <HeaderCell className={tablestyles.customHeader}>
                          View
                        </HeaderCell>
                        <Cell>
                          {(rowData) => {
                            return rowData.status === "Active" ? (
                              <Button
                                appearance="ghost"
                                color="green"
                                size="sm"
                              >
                                Active
                              </Button>
                            ) : (
                              <Button
                                appearance="ghost"
                                color="orange"
                                size="sm"
                              >
                                Disabled
                              </Button>
                            );
                          }}
                        </Cell>
                      </Column>

                      {/* Edit Button */}
                      <Column width={100} align="center">
                        <HeaderCell>...</HeaderCell>
                        <Cell style={{ padding: "3px" }}>
                          {(rowData) => (
                            <Button
                              appearance="primary"
                              startIcon={<EditIcon />}
                              onClick={() => {
                                setFormikInitialValues({
                                  displayName: rowData.displayName,
                                  email: rowData.email,
                                  organization: rowData.organization,
                                  password: "DummyPassword123!",
                                  roles: rowData.roles
                                    .split(", ")
                                    .map((role) => role.trim()),
                                  documentId: rowData.documentId,
                                });
                                setMode("EDIT");
                              }}
                            >
                              Edit
                            </Button>
                          )}
                        </Cell>
                      </Column>

                      {/* Enable/Disable Button */}
                      <Column width={120} align="center">
                        <HeaderCell>...</HeaderCell>
                        <Cell style={{ padding: "3px" }}>
                          {(rowData) => {
                            return rowData.status === "Active" ? (
                              <Button
                                appearance="primary"
                                startIcon={<CloseOutlineIcon />}
                                color="orange"
                                onClick={() => {
                                  disableAuthUser({ uid: rowData.documentId });
                                  toaster.push(messageAccountDeactivated, {
                                    placement: "bottomEnd",
                                  });
                                  updateTable();
                                }}
                              >
                                Deactivate
                              </Button>
                            ) : (
                              <Button
                                appearance="primary"
                                startIcon={<CheckOutlineIcon />}
                                color="green"
                                onClick={() => {
                                  enableAuthUser({ uid: rowData.documentId });
                                  toaster.push(messageAccountActivated, {
                                    placement: "bottomEnd",
                                  });
                                  updateTable();
                                }}
                              >
                                Activate
                              </Button>
                            );
                          }}
                        </Cell>
                      </Column>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Grid>
          <DashboardFooter />
        </>
      </AllowedTo>
    </DashboardLayout>
  );
}

export default Users;
